import { useState } from "react";
import { BetaFormData as FormData } from "../types/BetaFormData";
import {
  submitBetaSignup,
  BetaSignupError,
} from "../service/BetaTesterFormService";
import { logFormError } from "../lib/errorLogger";

export default function BetaTesterForm() {
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    readingMethod: [],
    favoriteGenres: [],
    phoneOS: "",
    thoughts: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  const readingMethods = ["Physical", "Ebook", "Audiobook", "Mix"];
  const genres = [
    "Fiction",
    "Fantasy",
    "Romance",
    "Mystery",
    "Sci-Fi",
    "Non-Fiction",
    "Historical",
    "Thriller",
    "Contemporary",
    "Literary Fiction",
    "Horror",
    "Young Adult",
  ];
  const phoneOSOptions = ["Apple", "Android", "Other"];

  const handleReadingMethodChange = (method: string) => {
    setFormData((prev) => ({
      ...prev,
      readingMethod: prev.readingMethod.includes(method)
        ? prev.readingMethod.filter((m) => m !== method)
        : [...prev.readingMethod, method],
    }));
  };

  const handleGenreChange = (genre: string) => {
    setFormData((prev) => ({
      ...prev,
      favoriteGenres: prev.favoriteGenres.includes(genre)
        ? prev.favoriteGenres.filter((g) => g !== genre)
        : [...prev.favoriteGenres, genre],
    }));
  };

  function validateForm(data: FormData): string | null {
    if (!data.firstName.trim()) return "First name is required.";
    if (!data.lastName.trim()) return "Last name is required.";
    if (!data.email.trim()) return "Email is required.";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email))
      return "Please enter a valid email address.";

    if (!data.phoneOS) return "Please select your phone OS.";

    if (data.thoughts.length > 500)
      return "Thoughts must be under 500 characters.";

    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const validationError = validateForm(formData);
    if (validationError) {
      setSubmitStatus({ type: "error", message: validationError });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });

    try {
      await submitBetaSignup(formData);

      setSubmitStatus({
        type: "success",
        message:
          "Thank you for signing up! Check your email for a confirmation message.",
      });

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        readingMethod: [],
        favoriteGenres: [],
        phoneOS: "",
        thoughts: "",
      });
    } catch (error) {
      // Log error to Vercel with structured context
      logFormError(error, "BetaTesterForm", {
        firstName: formData.firstName,
        email: formData.email,
        phoneOS: formData.phoneOS,
      });

      let errorMessage = "Something went wrong. Please try again.";

      if (error instanceof BetaSignupError) {
        errorMessage = error.message;
      }

      setSubmitStatus({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section id="beta-list" className="bg-primary py-20">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Become a Founding Reader
          </h2>
          <p className="text-gray-600 text-lg">
            Bookshelf is currently being crafted with care. Join our beta list
            to get early access and help shape the future of the app.
          </p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                placeholder="Your first name..."
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                placeholder="Your last name..."
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                placeholder="example@domain.com"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Which phone OS are you currently using?{" "}
                <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.phoneOS}
                onChange={(e) =>
                  setFormData({ ...formData, phoneOS: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                required
                disabled={isSubmitting}
              >
                <option value="">Select an option</option>
                {phoneOSOptions.map((os) => (
                  <option key={os} value={os}>
                    {os}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                How do you usually read?
              </label>
              <div className="grid grid-cols-2 gap-3">
                {readingMethods.map((method) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => handleReadingMethodChange(method)}
                    disabled={isSubmitting}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      formData.readingMethod.includes(method)
                        ? "bg-accent text-white"
                        : "border-2 border-gray-300 text-gray-700 hover:border-accent"
                    } ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {method}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Favorite Genres
              </label>
              <div className="flex flex-wrap gap-2">
                {genres.map((genre) => (
                  <button
                    key={genre}
                    type="button"
                    onClick={() => handleGenreChange(genre)}
                    disabled={isSubmitting}
                    className={`px-4 py-2 rounded-full font-medium transition ${
                      formData.favoriteGenres.includes(genre)
                        ? "bg-accent text-white"
                        : "border-2 border-gray-300 text-gray-700 hover:border-accent"
                    } ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What would make a reading app feel magical to you?{" "}
                <span className="text-gray-500 font-normal">(Optional)</span>
              </label>
              <textarea
                value={formData.thoughts}
                onChange={(e) =>
                  setFormData({ ...formData, thoughts: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                rows={5}
                placeholder="Share your thoughts..."
                disabled={isSubmitting}
              />
            </div>

            {submitStatus.type && (
              <div
                className={`p-4 rounded-lg ${
                  submitStatus.type === "success"
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                {submitStatus.message}
              </div>
            )}

            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-accent hover:bg-accent-dark text-white font-bold py-3 rounded-lg transition ${
                  isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isSubmitting ? "Joining..." : "Join the Beta"}
              </button>
              <p className="text-center text-gray-500 text-sm mt-3">
                We'll only email you about early access and updates.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
